(function() {
  'use strict';

  /* 2026-05-14 v4: Stacked accordion redesign + regex root-cause fix.
     CHANGES from v3:
       - Regex bug fix: previous (?:^|[\s\n>]) leading anchor was failing on concatenated text
         like "SizesSTYLE" where the section header is preceded by a lowercase letter rather than
         whitespace. Replaced with \b word boundary which matches at lowercase→uppercase transitions.
       - Layout: stacked single-column accordion (not 4-column grid). Each section is one full-width
         row with a title bar + chevron toggle. Click header to expand/collapse independently.
         Multiple rows can be open at once; opening one does NOT close others.
       - Smooth max-height transition on expand. Clear visual state for collapsed/expanded.
   */

  /* Word boundary anchor (\b) handles BOTH: (a) start-of-string, (b) whitespace before, (c) lowercase→uppercase boundary. */
  /* CRITICAL BUG FIX 2026-05-14 v5: \b (word boundary) does NOT match between two letters because
     both are \w characters. For text like "HeightCOMFORT" or "SizesSTYLE", \b fails at the
     lowercase→uppercase transition because both 't'/'C' and 's'/'S' are word characters.
     
     Replaced \b with negative lookbehind (?<![A-Z]) — only requires that the PRECEDING char is NOT
     uppercase (could be lowercase letter, whitespace, punctuation, OR start-of-string). This
     correctly anchors on the START of an all-caps run, even when concatenated to lowercase prefix. */
  var HEADER_RE = /(?<![A-Z])([A-Z][A-Z&]{2,15}(?:\s+[A-Z][A-Z&]{2,15})?)(?=\s*[\u2023])/g;

  function detectHeaders(text) {
    var positions = [];
    var m;
    HEADER_RE.lastIndex = 0;
    while ((m = HEADER_RE.exec(text)) !== null) {
      var name = m[1].trim().replace(/^[\s&]+|[\s&]+$/g, '');
      if (name.length < 4) continue;
      positions.push({ name: name, headerStart: m.index, contentStart: m.index + m[0].length });
    }
    return positions;
  }

  function parseTextContent(text) {
    var positions = detectHeaders(text);
    if (positions.length < 2) return [];
    positions.sort(function(a, b) { return a.headerStart - b.headerStart; });

    var sections = [];
    for (var j = 0; j < positions.length; j++) {
      var start = positions[j].contentStart;
      var end = (j + 1 < positions.length) ? positions[j + 1].headerStart : text.length;
      var chunk = text.substring(start, end).trim();
      var rawItems = chunk.split('\u2023');
      var items = [];
      for (var k = 0; k < rawItems.length; k++) {
        var clean = rawItems[k].replace(/^[\s\u203A\u003E\u2022\u2013\u2014\u2219]+/, '').trim();
        if (clean.length === 0 || clean === positions[j].name) continue;
        var parts = clean.split(/(?<!\*)(?=\*+[A-Z])/);
        for (var p = 0; p < parts.length; p++) {
          var part = parts[p].trim();
          if (part.length >= 3) items.push(part);
        }
      }
      if (items.length > 0) {
        sections.push({ title: positions[j].name, items: items });
      }
    }
    return sections;
  }

  function buildStack(sections) {
    var stack = document.createElement('div');
    stack.className = 'hc-features-stack';
    var allFootnotes = [];

    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      var regularItems = [];
      for (var f = 0; f < sec.items.length; f++) {
        if (sec.items[f].indexOf('**') === 0) {
          allFootnotes.push(sec.items[f]);
        } else {
          regularItems.push(sec.items[f]);
        }
      }
      var row = document.createElement('div');
      row.className = 'hc-feature-section';
      /* v12 (2026-05-15): RESTORED auto-expand for SAFETY (first section). The previous
         visual misalignment was actually caused by Prestige's .prose * + * adding 22.5px
         margin-top to sections 2-4, not by SAFETY's expansion. Now that margins are fixed,
         SAFETY can auto-expand without causing visual problems. */
      row.setAttribute('data-expanded', i === 0 ? 'true' : 'false');

      var titleId = 'hc-feature-' + i + '-' + (sec.title.replace(/[^a-zA-Z]/g, '').toLowerCase());
      var bodyId = titleId + '-body';

      var initialAriaExpanded = i === 0 ? 'true' : 'false';
      /* v9 (2026-05-15): Icon now uses INLINE SVG octagon container (was CSS clip-path).
         Reason: clip-path approach was rendering invisible (clip math worked in computed
         style but the result wasn't visible on screen — root cause unclear, likely interaction
         with absolute-positioned pseudo + inline-flex parent). SVG <polygon> with fill+stroke
         is rock-solid — guaranteed visible navy ring + white interior at any size.
         The pictogram <img> sits OVER the SVG via the existing CSS layering. */
      var ICON_URLS = {
        'SAFETY': 'https://cdn.shopify.com/s/files/1/0256/9748/4905/files/Xena-Pictogram-Safety-512.png',
        'COMFORT': 'https://cdn.shopify.com/s/files/1/0256/9748/4905/files/Xena-Pictogram-Comfort-512.png',
        'STYLE': 'https://cdn.shopify.com/s/files/1/0256/9748/4905/files/Xena-Pictogram-Style-512-v2.png',
        'CONVENIENCE': 'https://cdn.shopify.com/s/files/1/0256/9748/4905/files/Xena-Pictogram-Convenience-512.png'
      };
      var iconUrl = ICON_URLS[sec.title.toUpperCase()] || '';
      /* v12 (2026-05-15): Octagon SVG with UNIFORM border thickness.
         Previous version had stroke-width:4 with polygon vertices at viewBox edges (0/100).
         Half the stroke (2px) extends outside polygon edges. For axis-aligned edges this got
         clipped by the viewBox bbox. For diagonal edges it didn't. Result: uneven border
         thickness around the perimeter — visible in screenshots.
         Fix: expand viewBox to give the stroke room to render fully on all sides.
         viewBox '-3 -3 106 106' = 3px margin all around (1.5x the half-stroke for safety).
         The polygon stays at 0-100, the FULL stroke renders without clipping. */
      var OCTAGON_SVG = '<svg class="hc-feature-section__octagon-svg" width="72" height="72" viewBox="-3 -3 106 106" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false"><polygon points="29.3,0 70.7,0 100,29.3 100,70.7 70.7,100 29.3,100 0,70.7 0,29.3" fill="#ffffff" stroke="#14283f" stroke-width="4" stroke-linejoin="miter"/></svg>';
      var iconSlot = iconUrl
        ? '<span class="hc-feature-section__icon" aria-hidden="true">' + OCTAGON_SVG + '<img class="hc-feature-section__pictogram" src="' + iconUrl + '" alt="" loading="lazy" /></span>'
        : '<span class="hc-feature-section__icon hc-feature-section__icon--empty" aria-hidden="true"></span>';
      row.innerHTML =
        '<button type="button" class="hc-feature-section__header" aria-expanded="' + initialAriaExpanded + '" aria-controls="' + bodyId + '" id="' + titleId + '">' +
          '<span class="hc-feature-section__label">' +
            iconSlot +
            '<span class="hc-feature-section__title">' + sec.title + '</span>' +
          '</span>' +
          '<span class="hc-feature-section__chevron" aria-hidden="true">+</span>' +
        '</button>' +
        '<div class="hc-feature-section__body" id="' + bodyId + '" role="region" aria-labelledby="' + titleId + '">' +
          '<ul class="hc-feature-list">' +
          regularItems.map(function(item) { return '<li>' + item + '</li>'; }).join('') +
          '</ul>' +
        '</div>';

      var header = row.querySelector('.hc-feature-section__header');
      header.addEventListener('click', function() {
        var rowEl = this.parentElement;
        var expanded = rowEl.getAttribute('data-expanded') === 'true';
        rowEl.setAttribute('data-expanded', expanded ? 'false' : 'true');
        this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });

      stack.appendChild(row);
    }

    var container = document.createElement('div');
    container.className = 'hc-features-wrapper';
    if (allFootnotes.length > 0) {
      var fnDiv = document.createElement('div');
      fnDiv.className = 'hc-features-footnotes';
      fnDiv.innerHTML = allFootnotes.map(function(fn) {
        return '<p class="hc-footnote">' + fn + '</p>';
      }).join('');
      container.appendChild(fnDiv);
    }
    container.appendChild(stack);
    return container;
  }

  function init() {
    var prose = document.querySelector('.product-content-below-gallery__description.prose');
    if (!prose) return;

    var fullText = prose.textContent;
    var positions = detectHeaders(fullText);
    if (positions.length < 2) {
      if (window.console) console.warn('[hc-pdp-features v4] Only ' + positions.length + ' header(s) detected. Need >=2 ALL-CAPS words followed by \u2023 bullet. Description renders as-is.');
      return;
    }

    var sections = parseTextContent(fullText);
    if (sections.length < 2) {
      if (window.console) console.warn('[hc-pdp-features v4] ' + positions.length + ' headers detected but only ' + sections.length + ' produced valid items. Grid NOT built.');
      return;
    }

    /* v13 (2026-05-29): Query BOTH <p> AND <div> direct children so the hiding logic
       works regardless of whether Shopify renders the description text in a <p> or <div>.
       Excludes our own .hc-features-wrapper and .product-description-video containers. */
    var headerNames = positions.map(function(p) { return p.name; });
    var candidates = prose.querySelectorAll(':scope > p, :scope > div:not(.hc-features-wrapper):not(.product-description-video)');
    var insertBeforeEl = null;
    var hitKeywordZone = false;
    /* 2026-06-01 v14: Track product illustration images that live INSIDE keyword-zone
       candidates (e.g. Omega EH wraps illustration + SAFETY bullets all in one <p>). We'll
       clone them out and re-insert after the accordion stack so they remain visible. */
    var extractedIllustrations = [];
    function looksLikeIllustration(img) {
      var src = (img.src || '').toLowerCase();
      var alt = (img.alt || '').toLowerCase();
      if (src.indexOf('xena-pictogram') !== -1) return false; // skip SAFETY/COMFORT/STYLE/CONVENIENCE icons
      return src.indexOf('illustration') !== -1 || alt.indexOf('illustration') !== -1 || alt.indexOf('feature benefit') !== -1;
    }
    for (var j = 0; j < candidates.length; j++) {
      var elText = candidates[j].textContent.trim();
      var containsKeyword = false;
      for (var m = 0; m < headerNames.length; m++) {
        if (elText.indexOf(headerNames[m]) !== -1) { containsKeyword = true; break; }
      }
      if (containsKeyword && !hitKeywordZone) {
        hitKeywordZone = true;
        insertBeforeEl = candidates[j];
      }
      if (hitKeywordZone) {
        /* 2026-06-01: pure image-only paragraphs (e.g. <p><img></p>) — leave them visible. */
        if (candidates[j].textContent.trim() === '' && candidates[j].querySelector('img')) {
          continue;
        }
        /* 2026-06-01: extract any large product illustration image out of this candidate
           BEFORE hiding it, so malformed descriptions (Omega EH) don't lose their illustration. */
        var imgsInside = candidates[j].querySelectorAll('img');
        for (var ii2 = 0; ii2 < imgsInside.length; ii2++) {
          if (looksLikeIllustration(imgsInside[ii2])) {
            var clone = imgsInside[ii2].cloneNode(true);
            clone.removeAttribute('style');
            extractedIllustrations.push(clone);
          }
        }
        candidates[j].style.display = 'none';
        candidates[j].classList.add('hc-features-processed');
      }
    }

    var stack = buildStack(sections);
    if (insertBeforeEl) {
      insertBeforeEl.parentNode.insertBefore(stack, insertBeforeEl);
    } else {
      prose.appendChild(stack);
    }

    /* 2026-06-01 v14: insert any extracted illustrations right after the accordion stack. */
    if (extractedIllustrations.length > 0) {
      var illustWrap = document.createElement('div');
      illustWrap.className = 'hc-extracted-illustrations';
      extractedIllustrations.forEach(function(img) {
        var pNew = document.createElement('p');
        pNew.appendChild(img);
        illustWrap.appendChild(pNew);
      });
      if (insertBeforeEl) {
        insertBeforeEl.parentNode.insertBefore(illustWrap, insertBeforeEl);
      } else {
        prose.appendChild(illustWrap);
      }
    }

    /* Hide duplicate text-wall siblings of the boot-illustration <img>.
       v9 (2026-05-15): Also skip imgs inside our own .hc-feature-section__icon wrappers —
       the pictogram <img> is a sibling of the SVG octagon in there, and this loop was
       hiding the SVG. */
    var allImgs = prose.querySelectorAll('img');
    for (var ii = 0; ii < allImgs.length; ii++) {
      var img = allImgs[ii];
      var imgParent = img.parentElement;
      if (!imgParent || imgParent === prose) continue;
      if (imgParent.classList && imgParent.classList.contains('hc-feature-section__icon')) continue;
      var siblings = Array.from(imgParent.childNodes);
      for (var si = 0; si < siblings.length; si++) {
        var sib = siblings[si];
        if (sib === img) continue;
        if (sib.nodeType === 1) {
          if (sib.tagName === 'IMG') continue;
          sib.style.display = 'none';
          sib.classList.add('hc-features-processed');
        } else if (sib.nodeType === 3) {
          sib.textContent = '';
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();